// src/MechanicalPage.js
import React, { useState } from 'react';
import { dedupeChecklist } from './utils/dedupeChecklist';

export default function MechanicalPage({ checklist, images }) {
  const colorBlue  = '#153BC6';
  const lightGray1 = '#f7f7f7';
  const lightGray2 = '#eaeaea';

  // State for image modal
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [zoomed, setZoomed] = useState(false);

  // Scroll to the Mechanical section of the Damage Report
  function scrollToDamageReport() {
    const target =
      document.getElementById('damage-mechanicaldamage') || // slugify("MechanicalDamage") → "mechanicaldamage"
      document.getElementById('damage-report');
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* -------- render helpers (icon & images) --------------- */
  function renderDamageIcon(severity) {
    const baseStyle = {
      display: 'inline-block',
      verticalAlign: 'middle',
    };

    if (severity === 'Light') {
      // use the MagnifyingGlass.png from public/
      return (
        <img
          src="/MagnifyingGlass.png"
          alt="Inspect"
          style={{
            ...baseStyle,
            width: '1.4rem',
            height: '1.4rem',
          }}
        />
      );
    }

    if (severity === 'Moderate') {
      return <span style={{ ...baseStyle, color: 'orange', fontSize: '1.4rem' }}>&#9888;</span>;
    }
    if (severity === 'Severe') {
      return <span style={{ ...baseStyle, color: 'red', fontSize: '1.8rem' }}>&#10007;</span>;
    }
    return <span style={{ ...baseStyle, color: 'green', fontSize: '1.4rem' }}>&#10003;</span>;
  }

  // Keys for mechanical images
  const mechanicalImageKeys = [
    'EmmisionStickerImage',
    'EngineBayImage',
    'UnderOilCapImage',
    'OilDipStickImage',
    'TransmissionDipStickImage',
    'BrakeFluidLevelImage',
    'CoolantLevelImage',
    'PowerSteeringLevelImage',
  ];

  // Build URL list
  const mechanicalUrls = mechanicalImageKeys
    .map((key) => images[key])
    .filter(Boolean);

  // Image modal handlers
  function openModal(idx) { setSelectedIndex(idx); setZoomed(false); }
  function closeModal()    { setSelectedIndex(null); setZoomed(false); }
  function toggleZoom()    { setZoomed((z) => !z); }
  function showPrev(e) {
    e.stopPropagation();
    if (selectedIndex > 0) {
      setSelectedIndex((i) => i - 1);
      setZoomed(false);
    }
  }
  function showNext(e) {
    e.stopPropagation();
    if (selectedIndex < mechanicalUrls.length - 1) {
      setSelectedIndex((i) => i + 1);
      setZoomed(false);
    }
  }

  return (
    <div
      className="report-page"
      style={{
        width: '8.5in',
        minHeight: '11in',
        margin: '0 auto',
        padding: '0.3in',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        boxSizing: 'border-box',
        fontFamily: 'Arial, sans-serif',
        position: 'relative',
      }}
    >
      <h2 style={{ marginBottom: '0.2in', color: colorBlue }}>
        Mechanical Inspection
      </h2>

      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
        {/* Left column: Checklist + Climate Control */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Mechanical Checklist */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Mechanical Checklist
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {dedupeChecklist(checklist).map((item, idx) => {
                  const bgColor = idx % 2 === 0 ? lightGray1 : lightGray2;
                  const isClickable =
                    item.DamageSeverity === 'Light' ||
                    item.DamageSeverity === 'Moderate' ||
                    item.DamageSeverity === 'Severe';
                  return (
                    <tr key={item.DropdownValueId} style={{ backgroundColor: bgColor }}>
                      <td style={{ padding: '4px 8px', verticalAlign: 'middle' }}>
                        {item.DropdownValue}
                      </td>
                      <td
                        style={{
                          padding: '4px 8px',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                          cursor: isClickable ? 'pointer' : 'default',
                        }}
                        onClick={isClickable ? scrollToDamageReport : undefined}
                        title={isClickable ? 'Jump to Damage Report' : undefined}
                      >
                        {renderDamageIcon(item.DamageSeverity)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column: Images + Fluid Condition Checks */}
        <div style={{ flex: '0 0 50%' }}>
          {/* Mechanical Images */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              marginBottom: '0.2in',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 4, fontSize: '1rem' }}>
              Mechanical Images
            </h3>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                rowGap: '4px',
                columnGap: '8px',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
              }}
            >
              {mechanicalImageKeys.map((key) => {
                const url = images[key];
                if (!url) return null;
                const idx = mechanicalUrls.indexOf(url);
                return (
                  <div
                    key={key}
                    style={{
                      width: '80px',
                      height: '80px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      overflow: 'hidden',
                      cursor: 'pointer',
                    }}
                    onClick={() => openModal(idx)}
                  >
                    <img
                      src={url}
                      alt={key}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Fluid Condition Checks */}
          <div
            style={{
              borderLeft: `5px solid ${colorBlue}`,
              paddingLeft: 8,
              backgroundColor: '#f9f9f9',
              padding: '10px',
            }}
          >
            <h3 style={{ color: colorBlue, marginBottom: 6, fontSize: '1rem' }}>
              Fluid Condition Checks
            </h3>
            {/* Engine Oil */}
            <p style={{ margin: '6px 0', fontWeight: 'bold' }}>Engine Oil</p>
            <div style={{ marginLeft: '20px' }}>
              <p style={{ margin: '4px 0' }}>Clean &amp; golden = recently changed</p>
              <p style={{ margin: '4px 0' }}>Dark but smooth = used but likely okay</p>
              <p style={{ margin: '4px 0' }}>
                Thick, gritty, or milky = may signal neglect or engine issues
              </p>
            </div>
            {/* Brake Fluid */}
            <p style={{ margin: '6px 0', fontWeight: 'bold' }}>Brake Fluid</p>
            <div style={{ marginLeft: '20px' }}>
              <p style={{ margin: '4px 0' }}>Clear to light yellow = fresh and healthy</p>
              <p style={{ margin: '4px 0' }}>Dark or dirty = may be old or contaminated</p>
              <p style={{ margin: '4px 0' }}>
                Low fluid = could point to a leak or worn brake parts
              </p>
            </div>
            {/* Cross-Contamination */}
            <p style={{ margin: '6px 0', fontWeight: 'bold' }}>Cross‑Contamination</p>
            <div style={{ marginLeft: '20px' }}>
              <p style={{ margin: '4px 0' }}>
                We inspect for signs of fluid cross‑contamination, like oil mixing with coolant
                or vice versa.
              </p>
              <p style={{ margin: '4px 0' }}>Milky residue on the oil cap or dipstick</p>
              <p style={{ margin: '4px 0' }}>Sludgy buildup in the coolant reservoir</p>
              <p style={{ margin: '4px 0' }}>
                These can be warning signs of engine issues, such as a blown head gasket or
                failed oil cooler.
              </p>
            </div>
            {/* Transmission Fluid */}
            <p style={{ margin: '6px 0', fontWeight: 'bold' }}>Transmission Fluid</p>
            <div style={{ marginLeft: '20px' }}>
              <p style={{ margin: '4px 0' }}>
                If a transmission dipstick is present, we check the fluid’s color and smell:
              </p>
              <p style={{ margin: '4px 0' }}>Bright red and clean = healthy</p>
              <p style={{ margin: '4px 0' }}>Dark or burnt-smelling = may indicate overheating or aging fluid</p>
            </div>
            <p style={{ margin: '6px 0' }}>
              Visual checks give valuable clues but are not lab tests. If something seems off,
              we’ll flag it and recommend review by a qualified mechanic.
            </p>
          </div>
        </div>
      </div>

      {/* Logo */}
      <div style={{ position: 'absolute', bottom: 10, right: 10 }}>
        <img src="/logo.png" alt="Company Logo" style={{ height: 40 }} />
      </div>

      {/* Image Modal Overlay */}
      {selectedIndex !== null && mechanicalUrls[selectedIndex] && (
        <div
          onClick={closeModal}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
        >
          <img
            src={mechanicalUrls[selectedIndex]}
            alt="Enlarged"
            onClick={(e) => {
              e.stopPropagation();
              toggleZoom();
            }}
            style={{
              maxWidth: zoomed ? '95%' : '50%',
              maxHeight: zoomed ? '95%' : '60%',
              border: '2px solid #fff',
              borderRadius: 6,
              cursor: zoomed ? 'zoom-out' : 'zoom-in',
              transition: 'all 0.3s ease',
            }}
          />

          {selectedIndex > 0 && (
            <div
              onClick={showPrev}
              style={{
                position: 'absolute',
                left: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10094;
            </div>
          )}
          {selectedIndex < mechanicalUrls.length - 1 && (
            <div
              onClick={showNext}
              style={{
                position: 'absolute',
                right: 20,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'white',
                fontSize: '3rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                userSelect: 'none',
              }}
            >
              &#10095;
            </div>
          )}
          <div
            onClick={(e) => {
              e.stopPropagation();
              closeModal();
            }}
            style={{
              position: 'absolute',
              top: 10,
              right: 20,
              color: 'white',
              fontSize: '2rem',
              fontWeight: 'bold',
              cursor: 'pointer',
            }}
          >
            &times;
          </div>
        </div>
      )}
    </div>
  );
}
